import { useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import usePlatformSettings from '../../../hooks/usePlatformSettings'

import CollapsibleSection from '../components/CollapsibleSection'

import { PortfolioDecisionNarrative } from '../components/PortfolioDecisionNarrative'

import { CapitalAllocationOverview } from '../decision_center/components/capital_allocation'
import { InvestmentGovernanceEvidence, ValueRealizationContext } from '../decision_center/components/governance'
import { persistManagementInvestmentRequest } from '../../investment_governance/investment_proposal/api'
import { handoffPlanningCycle } from '../../investment_governance/investment_project/api'
import { evaluateManagementCapitalConstraint } from '../portfolio_analysis/services/portfolioCapitalOptimizationApi'
import { compareManagementConstrainedOptimization } from '../portfolio_analysis/contracts/managementConstrainedComparison'
import ManagementConstrainedOptimization from '../components/ManagementConstrainedOptimization'
import { clearManagementConstrainedDecisionEvidence, isManagementConstrainedDecisionReady, persistManagementConstrainedDecisionEvidence } from '../portfolio_analysis/contracts/managementConstrainedDecisionEvidence'

const PortfolioDecisionCenter = ({
    embedded = false,
    settings: providedSettings,
    activeScenarios,
    decisionCenterContext,
    portfolioAnalysisLoading = false,
    portfolioAnalysisError = null,
    acceptedOptimization,
    onDecisionGenerated,
    onReviewScenarios
}) => {
    const navigate = useNavigate()
    const { settings: localSettings, settingsLoaded, settingsError } = usePlatformSettings()

    const settings = providedSettings || localSettings

    const [portfolioBudget, setPortfolioBudget] = useState('')
    const [constrainedEvaluation, setConstrainedEvaluation] = useState({
        status: 'IDLE',
        result: null,
        reason: null,
        requestedCapital: null
    })
    const constrainedRequestSequence = useRef(0)
    const [handoffStatus, setHandoffStatus] = useState('unavailable')
    const [handoffError, setHandoffError] = useState('')
    const portfolioEngine = decisionCenterContext?.engines?.portfolio
    const authoritativePortfolioData = portfolioEngine?.data
    const unconstrainedResult = authoritativePortfolioData?.unconstrainedEconomicOptimization
    const investmentPolicy = authoritativePortfolioData?.combinedBundleOptimization?.policy
    const recommendationMetadata = portfolioEngine?.executiveRecommendation?.metadata

    const capitalRequirementReference = (
        <div className="rounded-xl border border-slate-200 bg-white p-5 shadow-sm sm:p-6">
            <CapitalAllocationOverview
                loading={portfolioAnalysisLoading}
                error={portfolioAnalysisError}
                unconstrainedResult={unconstrainedResult}
                policy={investmentPolicy}
                currency={decisionCenterContext?.metadata?.currency || settings?.currency}
                comparisonHorizon={recommendationMetadata?.comparisonHorizon}
                capitalBasis={recommendationMetadata?.capitalBasis}
            />
        </div>
    )

    const handleRunDecision = async () => {
        const requestSequence = ++constrainedRequestSequence.current
        const acceptedEvidence = acceptedOptimization?.status === 'AVAILABLE' ? acceptedOptimization.evidence : null
        const acceptedRun = acceptedEvidence?.optimizerResponse?.run_context
        setConstrainedEvaluation({ status: 'RUNNING', result: null, reason: null, requestedCapital: portfolioBudget })
        if (!acceptedEvidence || !acceptedRun) {
            setConstrainedEvaluation({
                status: 'ERROR',
                result: null,
                reason: 'ACCEPTED_OPTIMIZER_EVIDENCE_UNAVAILABLE',
                requestedCapital: portfolioBudget
            })
            return
        }
        try {
            const result = await evaluateManagementCapitalConstraint({
                availableCapital: portfolioBudget,
                scenarioIds: acceptedRun.scenario_ids,
                simulations: acceptedRun.simulations,
                seed: acceptedRun.seed
            })
            if (requestSequence !== constrainedRequestSequence.current) return
            const comparison = compareManagementConstrainedOptimization(acceptedEvidence, result)
            if (comparison.status !== 'COMPARABLE') {
                setConstrainedEvaluation({
                    status: 'INCOMPARABLE',
                    result: null,
                    reason: comparison.reason,
                    requestedCapital: portfolioBudget
                })
                return
            }
            const readyEvaluation = { status: 'READY', result, reason: null, requestedCapital: portfolioBudget }
            setConstrainedEvaluation(readyEvaluation)
            setHandoffStatus('persisting')
            try {
                await persistManagementInvestmentRequest(portfolioBudget, result.run_context.currency, acceptedEvidence.evidenceReference)
                const isReady = isManagementConstrainedDecisionReady({ evaluation: readyEvaluation, acceptedEvidence, currentRequestedCapital: portfolioBudget, persistenceStatus: 'ready' })
                if (isReady) persistManagementConstrainedDecisionEvidence({ acceptedEvidence, constrainedResponse: result, requestedCapital: portfolioBudget })
                setHandoffStatus(isReady ? 'ready' : 'unavailable')
            } catch (_) {
                setHandoffStatus('failed')
                return
            }
            onDecisionGenerated?.(result)
        } catch (requestError) {
            if (requestSequence !== constrainedRequestSequence.current) return
            setConstrainedEvaluation({
                status: 'ERROR',
                result: null,
                reason: requestError.message || 'REQUEST_FAILED',
                requestedCapital: portfolioBudget
            })
            setHandoffStatus('failed')
        }
    }

    const retryHandoffPersistence = async () => {
        const acceptedEvidence = acceptedOptimization?.status === 'AVAILABLE' ? acceptedOptimization.evidence : null
        if (!isManagementConstrainedDecisionReady({ evaluation: constrainedEvaluation, acceptedEvidence, currentRequestedCapital: portfolioBudget, persistenceStatus: 'ready' })) {
            setHandoffStatus('unavailable')
            return
        }
        setHandoffStatus('persisting')
        try {
            await persistManagementInvestmentRequest(portfolioBudget, constrainedEvaluation.result.run_context.currency, acceptedEvidence.evidenceReference)
            persistManagementConstrainedDecisionEvidence({ acceptedEvidence, constrainedResponse: constrainedEvaluation.result, requestedCapital: portfolioBudget })
            setHandoffStatus('ready')
        } catch (_) {
            setHandoffStatus('failed')
        }
    }

    const continueToInvestmentPlanning = async () => {
        const acceptedEvidence = acceptedOptimization?.status === 'AVAILABLE' ? acceptedOptimization.evidence : null
        if (!isManagementConstrainedDecisionReady({ evaluation: constrainedEvaluation, acceptedEvidence, currentRequestedCapital: portfolioBudget, persistenceStatus: 'ready' })) {
            setHandoffStatus('unavailable')
            return
        }
        setHandoffStatus('persisting')
        setHandoffError('')
        try {
            const constrainedEvidence = {
                schemaVersion: 1,
                authority: 'PORTFOLIO_CAPITAL_OPTIMIZATION',
                evaluationStatus: 'READY',
                comparisonStatus: 'COMPARABLE',
                persistenceStatus: 'ready',
                requestedCapital: Number(portfolioBudget),
                acceptedResponse: acceptedEvidence.optimizerResponse,
                constrainedResponse: constrainedEvaluation.result
            }
            const cycle = await handoffPlanningCycle({
                amount: portfolioBudget,
                currency: constrainedEvaluation.result.run_context.currency,
                accepted_optimization_evidence: acceptedEvidence,
                constrained_optimization_evidence: constrainedEvidence
            })
            navigate(`/investment-projects?planningCycle=${encodeURIComponent(cycle.cycle_reference)}`)
        } catch (error) {
            setHandoffStatus('failed')
            setHandoffError(error.response?.data?.detail || 'The authoritative Planning cycle could not be created. Retry the handoff.')
        }
    }

    const investmentConstraints = (
        <div
            id="portfolio-investment-input"
            className={'rounded-xl border border-slate-200 ' + 'bg-white p-5 shadow-sm'}
        >
            <div className="mb-5">
                <p className="text-xs font-semibold uppercase tracking-[0.16em] text-slate-500">
                    How much capital does Management intend to request?
                </p>
                <h2 className="mt-2 text-lg font-semibold text-slate-950">Management Requested Capital</h2>
                <p className="mt-2 max-w-4xl text-sm leading-6 text-slate-600">
                    Enter the capital Management intends to take forward for Board consideration after considering the
                    modeled optimum, executable initiatives, implementation capacity, timing, resources, priorities, and
                    organizational constraints. Capital Strategy evaluates this editable request; it is not
                    Board-approved, committed, released, or spent capital.
                </p>
            </div>
            <div className={'flex flex-col gap-4 ' + 'lg:flex-row lg:items-end'}>
                <div className="w-full max-w-md space-y-2">
                    <label htmlFor="portfolio-budget" className={'block text-sm font-medium ' + 'text-slate-700'}>
                        Management Requested Capital
                    </label>

                    <input
                        id="portfolio-budget"
                        type="number"
                        min="0"
                        value={portfolioBudget}
                        onChange={(event) => {
                            constrainedRequestSequence.current += 1
                            clearManagementConstrainedDecisionEvidence()
                            setPortfolioBudget(event.target.value)
                            const hasPriorEvidence = constrainedEvaluation.status === 'READY'
                            if (constrainedEvaluation.status === 'RUNNING') {
                                setConstrainedEvaluation({ status: 'IDLE', result: null, reason: null, requestedCapital: null })
                            }
                            setHandoffStatus(hasPriorEvidence ? 'stale' : 'unavailable')
                        }}
                        className={
                            'w-full rounded-lg border ' +
                            'border-slate-300 px-3 py-2 ' +
                            'outline-none transition ' +
                            'focus:border-slate-500 ' +
                            'focus:ring-2 ' +
                            'focus:ring-slate-200'
                        }
                    />
                </div>

                <button
                    type="button"
                    onClick={handleRunDecision}
                    disabled={constrainedEvaluation.status === 'RUNNING'}
                    className={
                        'rounded-lg bg-slate-900 ' +
                        'px-5 py-2.5 text-sm ' +
                        'font-medium text-white ' +
                        'transition hover:bg-slate-800 ' +
                        'disabled:cursor-not-allowed ' +
                        'disabled:opacity-50'
                    }
                >
                    {constrainedEvaluation.status === 'RUNNING' ? 'Evaluating...' : 'Evaluate Capital Allocation'}
                </button>
            </div>
            <div className="mt-5">
                <ManagementConstrainedOptimization
                    evaluation={constrainedEvaluation}
                    requestedCapital={constrainedEvaluation.requestedCapital}
                    acceptedResponse={acceptedOptimization?.evidence?.optimizerResponse}
                />
            </div>
        </div>
    )

    if (!providedSettings && settingsError) {
        return (
            <div className={embedded ? '' : 'p-6'}>
                <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                    Unable to load Platform Settings.
                </div>
            </div>
        )
    }

    if (!providedSettings && !settingsLoaded) {
        return (
            <div className={embedded ? '' : 'p-6'}>
                <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
                    Loading portfolio decision settings...
                </div>
            </div>
        )
    }

    return (
        <div className={embedded ? 'space-y-6' : 'min-h-screen bg-slate-50 p-6 space-y-6'}>
            {!embedded && (
                <div>
                    <h1 className={'text-2xl font-semibold ' + 'text-slate-950'}>Portfolio Decision Center</h1>

                    <p className="mt-1 text-sm text-slate-500">
                        Evaluate capital allocation alternatives and the digital risk trade-offs they create.
                    </p>
                </div>
            )}

            <PortfolioDecisionNarrative
                decision={null}
                activeScenarios={activeScenarios}
                currentPortfolioPosition={decisionCenterContext?.overallPosition}
                settings={settings}
                capitalRequirementReference={capitalRequirementReference}
                investmentConstraints={investmentConstraints}
                handoffStatus={handoffStatus}
                handoffVisible={handoffStatus !== 'unavailable'}
                onRetryHandoffPersistence={retryHandoffPersistence}
                onContinueToPlanning={continueToInvestmentPlanning}
                handoffError={handoffError}
            />

            <section aria-labelledby="governance-outcomes-title" className="space-y-4 border-t border-slate-300 pt-7">
                <div>
                    <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">
                        Level 3 · Contextual evidence
                    </p>
                    <h2 id="governance-outcomes-title" className="mt-2 text-lg font-semibold text-slate-900">
                        Governance & Outcomes
                    </h2>
                    <p className="mt-1 max-w-3xl text-sm text-slate-600">
                        Independent governance, realized-outcome and technical evidence remains available without
                        changing the analytical recommendation.
                    </p>
                </div>
                <CollapsibleSection
                    title="Investment Governance"
                    description="Authorization and governance context from the authoritative governance workspace."
                    defaultOpen={false}
                >
                    <InvestmentGovernanceEvidence engine={decisionCenterContext?.engines?.governance} />
                </CollapsibleSection>
                <CollapsibleSection
                    title="Value Realization Evidence"
                    description="Retrospective outcome evidence from Benefits Governance."
                    defaultOpen={false}
                >
                    <ValueRealizationContext context={decisionCenterContext?.valueRealizationContext} />
                </CollapsibleSection>
            </section>
        </div>
    )
}

export default PortfolioDecisionCenter
